import InfoIcon from '@mui/icons-material/Info';
import { Typography } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { useMemo } from 'react';
import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { Flex } from '../../../../base/components/Flex';
import FormikCheckBox from '../../../../base/components/form/FormikCheckBox';
import FormikForm from '../../../../base/components/form/FormikForm';
import FormikTextField from '../../../../base/components/form/FormikTextField';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';
import InfoTooltip from '../components/InfoTooltip';
import QrButton from '../components/QrButton';
import QrPageLogo from '../components/QrPageLogo';

export interface QrPersonalData {
  firstName: string;
  lastName: string;
  email: string;
  gdpr: boolean;
}

interface Props {
  data: QrPersonalData;
  onSubmit: (data: QrPersonalData) => void;
}

const QrPersonalStep = ({ data, onSubmit }: Props) => {
  const { _t } = useTranslationPrefix('LaunchPlaylistQrPageNew');
  const validationSchema = useMemo(() => getValidationSchema(_t), [_t]);

  const handleSubmit = async (
    values: QrPersonalData,
    helpers: FormikHelpers<QrPersonalData>,
  ) => {
    onSubmit(values);
  };

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight="100%"
      alignItems="center"
      gap={2}
      px={{ xs: 2, md: 6 }}
    >
      <Flex
        flexDirection="column"
        width="100%"
        alignItems={{ xs: 'center', md: 'end' }}
      >
        <QrPageLogo />
      </Flex>
      <Flex
        sx={{ my: 'auto', width: '100%' }}
        flexDirection="column"
        alignItems="center"
      >
        <Formik<QrPersonalData>
          initialValues={data}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, isValid, touched, errors }) => (
            <FormikForm sx={{ margin: 0, maxWidth: '100%', width: '30rem' }}>
              <Flex
                flexDirection="column"
                gap={{ xs: 3, sm: 4 }}
                alignItems="center"
              >
                <FormikTextField
                  fullWidth
                  name="firstName"
                  variant="outlined"
                  label={_t('firstName', 'First name')}
                  size="large"
                />
                <FormikTextField
                  fullWidth
                  name="lastName"
                  variant="outlined"
                  label={_t('lastName', 'Last name')}
                  size="large"
                />

                <FormikTextField
                  fullWidth
                  name="email"
                  variant="outlined"
                  label={_t('email', 'E-mail')}
                  size="large"
                />

                <Flex alignItems="center" justifyContent="center" gap={1}>
                  <FormikCheckBox
                    name="gdpr"
                    label={_t('gdprAgreement', 'GDPR agreement')}
                    labelSx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: { xs: '1rem', sm: '1.5rem' },
                      },
                    }}
                  />
                  <InfoTooltip
                    title={
                      <Flex
                        flexDirection="column"
                        gap={1}
                        maxHeight="60vh"
                        overflow="auto"
                      >
                        <Typography>
                          {_t(
                            'gdprAgreementParagraph1',
                            'By checking the box "GDPR Consent," I express my agreement with the processing of my personal data by VR Training s.r.o., registered at Karlovarská 451/70, 323 00, Plzeň, Company ID: 10995595, VAT ID: CZ10995595, as the data controller, for the purposes of registration and login to the VR Training web application.',
                          )}
                        </Typography>
                        <Typography>
                          {_t(
                            'gdprAgreementParagraph2',
                            'VR Training s.r.o. will process my personal data in accordance with Regulation (EU) 2016/679 of the European Parliament and the Council (GDPR) and will use this data solely for the purposes of providing services within this application, including user account management, ensuring security, and improving the user experience.',
                          )}
                        </Typography>
                        <Typography>
                          {_t(
                            'gdprAgreementParagraph3',
                            'I also agree that my data may be stored for the time necessary to provide these services. I have the right to withdraw my consent at any time by sending a request to support@vrtraining.cz. I acknowledge that withdrawing consent may limit the ability to use certain features of the web application.',
                          )}
                        </Typography>
                      </Flex>
                    }
                  >
                    <InfoIcon color="primary" sx={{ fontSize: '1.5rem' }} />
                  </InfoTooltip>
                </Flex>

                <QrButton
                  id="qr-next"
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {_t('personalNext', 'Next')}
                </QrButton>
              </Flex>
            </FormikForm>
          )}
        </Formik>
      </Flex>
    </Flex>
  );
};

const getValidationSchema = (t: TFunction) =>
  Yup.object({
    firstName: Yup.string()
      .trim()
      .typeError(t('firstNameRequired', 'First name is required'))
      .required(t('firstNameRequired', 'First name is required')),
    lastName: Yup.string()
      .trim()
      .typeError(t('lastNameRequired', 'Last name is required'))
      .required(t('lastNameRequired', 'Last name is required')),
    email: Yup.string()
      .typeError(t('emailRequired', 'Email is required'))
      .email(t('emailInvalid', 'Email address is invalid'))
      .required(t('emailRequired', 'Email is required')),
    gdpr: Yup.bool()
      .typeError(t('gdprRequired', 'GDPR agreement is required'))
      .required(t('gdprRequired', 'GDPR agreement is required'))
      .oneOf([true], t('gdprRequired', 'GDPR agreement is required')),
  });

export default QrPersonalStep;
